import React, { useEffect, useState } from "react";
import axios from "axios";
import TestRating from "./TestRating";
import {
  FaFlask,
  FaShoppingCart,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaTag,
  FaClock,
  FaVial,
  FaBuilding,
  FaClipboardList,
  FaHeart,
  FaRegHeart,
  FaRegClock,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useCartActions } from "../../hooks/useCartActions";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { openCartSlider } from "../../Redux/cartSliderSlice";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";
import LoginPopup from "../common/LoginPopup";

// ✅ Helper function to get test name
const getTestName = (testData) => {
  if (!testData) return "Unknown Test";
  return testData.testName || testData.TestName || testData.name || testData.Name || "Unknown Test";
};

export default function TestDetails({ id, onClose, initialTestData }) {
  const { id: paramId } = useParams();
  const finalId = id || paramId;
  const dispatch = useDispatch();

  // ✅ LOG - Component Mount
  console.log('🔬 TestDetails Component Mounted');
  console.log('📥 Received Props:', {
    id,
    paramId,
    finalId,
    initialTestData: initialTestData ? {
      TestID: initialTestData.TestID,
      TestName: initialTestData.TestName,
      hasData: true
    } : null
  });

  const [test, setTest] = useState(initialTestData || null);
  const [synonyms, setSynonyms] = useState([]);
  const [loading, setLoading] = useState(!initialTestData);
  const [error, setError] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 14;

  const API_BASE = (process.env.REACT_APP_API_URL ).replace(/\/+$/, "");
  const cart = useSelector((state) => state.cart.items || []);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  const { addWithReplaceCheck, removeItem, LoginPopupComponent } = useCartActions();

  // ✅ Get User ID from localStorage
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  };

  // ✅ Check if in wishlist
  const isInWishlist = (productId, categoryId = 2) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  // ✅ Load Wishlist
  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  // ✅ Toggle Wishlist
  const toggleWishlist = async (e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = Number(test?.testID || test?.TestID);
    const categoryId = 2;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ✅ Fetch Synonyms
  const fetchSynonyms = async () => {
    console.log('🔍 Fetching synonyms for Test ID:', finalId);
    if (!finalId) return;
    try {
      const synRes = await axios.get(
        `${API_BASE}/MasterAPI/TestSynonymsByTestId`,
        { params: { TestId: finalId, page: 1, pageSize: 100 } }
      );
      console.log('📊 Synonyms Response:', synRes?.data?.data?.length || 0, 'synonyms found');
      setSynonyms(synRes?.data?.data || []);
    } catch (error) {
      console.error('Error fetching synonyms:', error);
      setSynonyms([]);
    }
  };

  // ✅ Fetch Test Details
  const fetchTestDetails = async () => {
    console.log('🌐 Fetching test details for ID:', finalId);
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(
        `${API_BASE}/MasterAPI/TestMasterByTestId?TestId=${finalId}`
      );
      
      console.log('📡 API Response Status:', res.status);
      console.log('📦 API Response Data:', res.data);
      
      const testData = Array.isArray(res.data) ? res.data[0] : res.data;
      
      console.log('📊 Test Data:', testData);
      console.log('🆔 Test ID from API:', testData?.TestID || testData?.testID);
      console.log('📝 Test Name from API:', testData?.TestName || testData?.testName);
      
      if (testData) {
        // Normalize the data
        const normalizedTest = {
          ...testData,
          testName: testData.testName || testData.TestName,
          testID: testData.testID || testData.TestID,
          testPrice: testData.testPrice || testData.TestPrice,
          sellingPrice: testData.sellingPrice || testData.SellingPrice,
          labId: testData.labId || testData.LabId,
          isPopular: testData.isPopular || testData.IsPopular,
          isFasting: testData.isFasting || testData.IsFasting,
          reportTime: testData.reportTime !== undefined ? testData.reportTime : testData.ReportTime,
          description: testData.description || testData.Description,
          sampleName: testData.sampleName || testData.SampleName,
          methodName: testData.methodName || testData.MethodName,
          unitName: testData.unitName || testData.UnitName,
          formatName: testData.formatName || testData.FormatName,
          synonymName: testData.synonymName || testData.SynonymName,
          normalRange: testData.normalRange || testData.NormalRange,
          lab_name: testData.lab_name || testData.LabName,
          categoryName: testData.categoryName || testData.CategoryName,
          departmentName: testData.departmentName || testData.DepartmentName,
          testCode: testData.testCode || testData.TestCode,
        };
        
        console.log('✅ Normalized Test:', {
          id: normalizedTest.testID,
          name: normalizedTest.testName,
          labId: normalizedTest.labId
        });
        
        setTest(normalizedTest);
      } else {
        console.warn('⚠️ No test data found for ID:', finalId);
        setError("Test not found");
        setTest(null);
      }

      await fetchSynonyms();
      await loadWishlist();

    } catch (err) {
      console.error('❌ Error fetching test details:', err);
      console.error('❌ Error details:', err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to load test details");
      setTest(null);
      setSynonyms([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Main Effect
  useEffect(() => {
    console.log('🔄 useEffect triggered');
    console.log('📥 initialTestData:', initialTestData ? 'Yes' : 'No');
    console.log('🆔 finalId:', finalId);
    
    // If initialTestData is provided, use it directly
    if (initialTestData) {
      console.log('✅ Using initialTestData:', {
        id: initialTestData.TestID,
        name: initialTestData.TestName,
        labId: initialTestData.LabId
      });
      setTest(initialTestData);
      setLoading(false);
      fetchSynonyms();
      loadWishlist();
      return;
    }

    // Otherwise fetch from API
    if (!finalId) {
      console.warn('⚠️ No finalId available');
      return;
    }
    
    console.log('🌐 Fetching from API for ID:', finalId);
    fetchTestDetails();
  }, [finalId, API_BASE, initialTestData]);

  // ✅ LOG - When test state changes
  useEffect(() => {
    if (test) {
      console.log('🔄 Test state updated:', {
        id: test.TestID || test.testID,
        name: test.TestName || test.testName,
        hasData: true
      });
    }
  }, [test]);

  // Pagination helpers
  const totalPages = Math.ceil(synonyms.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, synonyms.length);
  const currentSynonyms = synonyms.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goToPage = (page) => setCurrentPage(page);

  const getPageNumbers = () => {
    const pages = [];
    const maxShow = 5;
    if (totalPages <= maxShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  // Loading / Error / No test states
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
        <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white z-50 shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-white border-b p-4 flex justify-between items-center">
            <h4 className="flex items-center gap-2 font-semibold">
              <FaFlask className="text-red-500" /> Error
            </h4>
            <button onClick={onClose} className="text-2xl">×</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold text-gray-800">Failed to Load</h3>
              <p className="text-sm text-gray-600 mt-2">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!test) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
        <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white z-50 shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-white border-b p-4 flex justify-between items-center">
            <h4 className="flex items-center gap-2 font-semibold">
              <FaFlask className="text-red-500" /> Test Details
            </h4>
            <button onClick={onClose} className="text-2xl">×</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="text-gray-400 text-5xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold text-gray-800">No Test Found</h3>
              <p className="text-sm text-gray-600 mt-2">Test with ID {finalId} not found</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Derived data
  const productId = Number(test?.testID || test?.TestID);
  const originalPrice = Number(test?.testPrice || test?.TestPrice) || 0;
  const sellingPrice = Number(test?.sellingPrice || test?.SellingPrice) || originalPrice;
  const discountAmount = originalPrice - sellingPrice;
  const discountPercent = originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0;
  const inCart = cart.some((item) => Number(item.productId) === productId);
  const inWishlist = isInWishlist(productId, 2);
  const isPopular = test?.isPopular === true || test?.IsPopular === true;
  const isFasting = test?.isFasting === true || test?.IsFasting === true;
  
  // ✅ LOG - Final test data being rendered
  console.log('🎨 Rendering TestDetails with:', {
    productId,
    testName: getTestName(test),
    originalPrice,
    sellingPrice,
    inCart,
    inWishlist
  });
  
  // Report Time
  const reportTime = test?.reportTime !== undefined && test?.reportTime !== null 
    ? test?.reportTime 
    : test?.ReportTime !== undefined && test?.ReportTime !== null 
      ? test?.ReportTime 
      : 0;

  const hasReportTime = reportTime !== undefined && reportTime !== null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-gray-50 z-50 shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="bg-white border-b px-5 py-3 flex justify-between items-center sticky top-0 z-10">
          <h4 className="flex items-center gap-2 font-semibold text-gray-800 text-sm uppercase tracking-wider">
            <FaFlask className="text-red-500" /> Test Details
          </h4>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleWishlist}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-200"
              disabled={wishlistLoading}
            >
              {wishlistLoading ? (
                <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                inWishlist ? (
                  <FaHeart size={18} className="text-red-500 fill-red-500" />
                ) : (
                  <FaRegHeart size={18} className="text-gray-500 hover:text-red-500" />
                )
              )}
            </button>
            <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-800 transition-colors">
              ×
            </button>
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          
          {/* HERO */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 relative">
            {/* Badges */}
            <div className="flex flex-wrap gap-1.5 absolute top-2 right-2">
              {isPopular && (
                <span className="bg-yellow-400 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow flex items-center gap-1">
                  <FaStar size={8} className="fill-white" /> POPULAR
                </span>
              )}
              {discountPercent > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow">
                  {discountPercent}% OFF
                </span>
              )}
            </div>
            {isFasting && (
              <div className="absolute top-2 left-2">
                <span className="bg-blue-500 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow flex items-center gap-1">
                  <FaClock size={8} /> Fasting
                </span>
              </div>
            )}

            <div className="mt-6">
              {/* ✅ Test Name - Using getTestName helper */}
              <h2 className="text-lg font-bold text-gray-800 leading-tight">
                {getTestName(test)}
              </h2>
              {test?.testCode && (
                <p className="text-[10px] text-gray-500 mt-0.5">Code: {test.testCode}</p>
              )}
              
              {/* Report Time */}
              {hasReportTime && (
                <div className={`flex items-center gap-1.5 mt-2 rounded-lg px-3 py-1.5 border ${
                  reportTime === 0 
                    ? 'bg-orange-50 border-orange-300' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <FaRegClock size={14} className={reportTime === 0 ? 'text-orange-500' : 'text-blue-600'} />
                  <span className={`text-xs font-medium ${reportTime === 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                    {reportTime === 0 ? (
                      '⚠️ Report Time: Not Available'
                    ) : (
                      `Report Time: ${reportTime} ${reportTime === 1 ? 'Hour' : 'Hours'}`
                    )}
                  </span>
                </div>
              )}

              {/* Chips */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {test?.lab_name && (
                  <span className="inline-flex items-center gap-1 text-[9px] bg-gray-50 text-gray-600 px-2 py-1 rounded-full border border-gray-200">
                    <FaBuilding size={9} /> {test.lab_name}
                  </span>
                )}
                {test?.categoryName && (
                  <span className="inline-flex items-center gap-1 text-[9px] bg-gray-50 text-gray-600 px-2 py-1 rounded-full border border-gray-200">
                    <FaTag size={9} /> {test.categoryName}
                  </span>
                )}
                {test?.departmentName && (
                  <span className="inline-flex items-center gap-1 text-[9px] bg-gray-50 text-gray-600 px-2 py-1 rounded-full border border-gray-200">
                    <FaClipboardList size={9} /> {test.departmentName}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* DESCRIPTION */}
          {test?.description && (
            <div className="bg-white rounded-xl shadow-sm p-3.5 border border-gray-100">
              <h6 className="text-[11px] font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <FaTag size={11} className="text-red-400" /> Description
              </h6>
              <p className="text-xs text-gray-600 leading-relaxed">{test.description}</p>
            </div>
          )}

          {/* TEST INFORMATION */}
          <div className="bg-white rounded-xl shadow-sm p-3.5 border border-gray-100">
            <h6 className="text-[11px] font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <FaVial size={11} className="text-red-400" /> Information
            </h6>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
              {test?.sampleName && (
                <>
                  <span className="text-gray-500">Sample</span>
                  <span className="font-medium text-gray-800 text-right">{test.sampleName}</span>
                </>
              )}
              {test?.methodName && (
                <>
                  <span className="text-gray-500">Method</span>
                  <span className="font-medium text-gray-800 text-right">{test.methodName}</span>
                </>
              )}
              {test?.unitName && (
                <>
                  <span className="text-gray-500">Unit</span>
                  <span className="font-medium text-gray-800 text-right">{test.unitName}</span>
                </>
              )}
              {test?.formatName && (
                <>
                  <span className="text-gray-500">Format</span>
                  <span className="font-medium text-gray-800 text-right">{test.formatName}</span>
                </>
              )}
              {test?.synonymName && (
                <>
                  <span className="text-gray-500">Synonyms</span>
                  <span className="font-medium text-gray-800 text-right break-words">{test.synonymName}</span>
                </>
              )}
              {test?.normalRange && (
                <>
                  <span className="text-gray-500">Normal Range</span>
                  <span className="font-medium text-gray-800 text-right break-words">{test.normalRange}</span>
                </>
              )}
              {hasReportTime && (
                <>
                  <span className="text-gray-500">Report Time</span>
                  <span className={`font-medium text-right ${
                    reportTime === 0 ? 'text-orange-600 font-bold' : 'text-gray-800'
                  }`}>
                    {reportTime === 0 ? '⚠️ Not Available' : `${reportTime} ${reportTime === 1 ? 'Hour' : 'Hours'}`}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* SYNONYMS / RELATED TESTS */}
          {synonyms.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-3.5 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <h6 className="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5">
                  <FaFlask size={11} className="text-red-400" /> Related Tests
                </h6>
                <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {synonyms.length}
                </span>
              </div>

              <Slider
                {...{
                  dots: false,
                  infinite: currentSynonyms.length > 3,
                  speed: 500,
                  slidesToShow: 2,
                  slidesToScroll: 1,
                  arrows: true,
                  responsive: [{ breakpoint: 768, settings: { slidesToShow: 1 } }],
                }}
              >
                {currentSynonyms.map((s, i) => (
                  <div key={i} className="px-1">
                    <div className="p-2 bg-gray-50 rounded-lg text-center hover:shadow-md transition-shadow">
                      <p className="text-[11px] font-medium text-gray-700 truncate">
                        {s.synonymName || s.TestName || "Related Test"}
                      </p>
                    </div>
                  </div>
                ))}
              </Slider>

              {totalPages > 1 && (
                <>
                  <div className="flex justify-center items-center gap-1.5 mt-2.5 pt-2 border-t border-gray-100">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className={`px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors duration-200 text-[10px] ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      <FaChevronLeft size={8} /> Prev
                    </button>
                    <div className="flex gap-0.5">
                      {getPageNumbers().map((page, idx) => (
                        <button
                          key={idx}
                          onClick={() => typeof page === "number" && goToPage(page)}
                          className={`px-2.5 py-1 rounded-md transition-colors duration-200 text-[10px] min-w-[24px] ${
                            currentPage === page
                              ? "bg-red-600 text-white"
                              : page === "..."
                              ? "bg-gray-100 text-gray-600 cursor-default"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                          disabled={page === "..."}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className={`px-2.5 py-1 rounded-md flex items-center gap-1 transition-colors duration-200 text-[10px] ${
                        currentPage === totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Next <FaChevronRight size={8} />
                    </button>
                  </div>
                  <div className="text-center text-[9px] text-gray-400 mt-1">
                    {startIndex + 1} - {endIndex} of {synonyms.length}
                  </div>
                </>
              )}
            </div>
          )}

          {/* RATING */}
          <div className="bg-white rounded-xl shadow-sm p-3.5 border border-gray-100">
            <TestRating productId={finalId} categoryId={2} showLabel={true} size="sm" />
          </div>
        </div>

        {/* FOOTER */}
        <div className="border-t bg-white px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-green-600">₹{sellingPrice}</span>
                {discountAmount > 0 && (
                  <span className="text-sm line-through text-gray-400">₹{originalPrice}</span>
                )}
              </div>
              {discountAmount > 0 && (
                <span className="text-xs text-red-500 font-medium">Save ₹{discountAmount} ({discountPercent}%)</span>
              )}
              {hasReportTime && (
                <div className={`flex items-center gap-1 mt-0.5 ${
                  reportTime === 0 ? 'text-orange-500' : 'text-blue-600'
                }`}>
                  <FaRegClock size={10} />
                  <span className="text-[9px] font-medium">
                    {reportTime === 0 ? '⚠️ Report Time: Not Available' : `Reports in ${reportTime} ${reportTime === 1 ? 'Hour' : 'Hours'}`}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                if (onClose) onClose();
                setTimeout(() => dispatch(openCartSlider()), 100);
              }}
              className="text-[11px] font-medium text-green-600 border border-green-600 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors"
            >
              View Cart
            </button>
          </div>
          <button
            onClick={() => {
              const payload = {
                productId,
                categoryId: 2,
                price: sellingPrice,
                storeId: Number(test?.labId || test?.LabId) || 0,
                productName: getTestName(test),
              };
              inCart ? removeItem(payload) : addWithReplaceCheck(payload);
            }}
            className={`w-full py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
              inCart
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            <FaShoppingCart size={14} />
            {inCart ? "Remove from Cart" : "Add to Cart"}
          </button>
        </div>
      </div>

      {/* Login Popup */}
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
      />

      {LoginPopupComponent}
    </>
  );
}